/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useRef, useState, useEffect } from "react";
import axios from "axios";
import { OnmaxContext } from "../OnmaxContext";
import { useNavigate } from "react-router-dom";

export default function OTPModal() {
  const {
    toastSuccess,
    toastError,
  } = useContext(OnmaxContext);
  const [show, setShow] = useState(true);
  const [timer, setTimer] = useState(180); 
  const [canResend, setCanResend] = useState(false);
  const email = localStorage.getItem("email");
  const navigate = useNavigate();
  const inputsRef = useRef([]);
  const [isVerifying, setIsVerifying] = useState(false);

  // 🔥 TIMER STARTS WHEN MODAL OPENS
  useEffect(() => {
    if (!show) return;

    setTimer(180);
    setCanResend(false);

    const interval = setInterval(() => {
      setTimer((sec) => {
        if (sec <= 1) {
          setCanResend(true);
          clearInterval(interval);
          return 0;
        }
        return sec - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [show]);

  const formatTime = () => {
    const minutes = Math.floor(timer / 60);
    const seconds = timer % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const resendOTP = async () => {
    try {
      const res = await axios.post(process.env.REACT_APP_BACKEND_LINK, {
        method: "user",
        submethod: "rsend",
        key: process.env.REACT_APP_KEY,
        email: email
      });

      if (res.data.resCode === 200) {
        toastSuccess("OTP Resent!");
      }
      inputsRef.current.forEach((i) => (i.value = "")); // Clear inputs
      setTimer(180);
      setCanResend(false);
    } catch (error) {
      toastError("Failed to resend OTP");
    }
  };

  const verifyOTP = async (e) => {
    e.preventDefault();
    if (isVerifying) return;

    const otp = inputsRef.current.map((i) => i.value).join("");

    if (otp.length !== 6) {
      toastError("Please enter a valid 6-digit OTP");
      return;
    }

    try {
      setIsVerifying(true);
      const res = await axios.post(process.env.REACT_APP_BACKEND_LINK, {
        method: "user",
        submethod: "verifyemail",
        key: process.env.REACT_APP_KEY,
        email: email,
        otp: otp,
      });

      if (res.data?.resCode === 200) {
        toastSuccess(res.data.message || "OTP verified successfully!");
        setShow(false);
        navigate("/");
      } else {
        toastError(res.data.message || "Invalid OTP, try again.");
      }
    } catch (error) {
      toastError("Something went wrong!");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleInput = (e, index) => {
    const value = e.target.value;
    if (!/^[0-9]?$/.test(value)) {
      e.target.value = "";
      return;
    }
    if (value && index < 5) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const customStyles = `
    .ai-modal-overlay {
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(15, 23, 42, 0.4);
      backdrop-filter: blur(8px);
      z-index: 1050;
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
    }

    .ai-auth-card {
        background: #ffffff;
        border-radius: 24px;
        padding: 40px;
        width: 100%;
        max-width: 480px;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(0, 0, 0, 0.05);
        position: relative;
    }

    .ai-close-btn {
      position: absolute; top: 20px; right: 20px;
      width: 32px; height: 32px; border-radius: 50%;
      background: #f1f5f9; border: none; color: #64748b;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; transition: 0.3s;
    }
    .ai-close-btn:hover { background: #e2e8f0; color: #1e293b; }

    .ai-icon-bubble {
      width: 72px; height: 72px;
      background: rgba(16, 185, 129, 0.1);
      border-radius: 20px;
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 24px;
      color: #10b981; font-size: 28px;
    }

    .ai-title { color: #1a2234; text-align: center; font-size: 24px; font-weight: 800; margin-bottom: 8px; }
    .ai-subtitle { color: #64748b; text-align: center; font-size: 14px; margin-bottom: 32px; line-height: 1.6; }

    .ai-otp-grid {
      display: grid; grid-template-columns: repeat(6, 1fr);
      gap: 10px; margin-bottom: 30px;
    }

    .ai-otp-input {
      width: 100%; height: 54px;
      background: #f8fafc; border: 1px solid #e2e8f0;
      border-radius: 12px; color: #1a2234;
      font-size: 22px; font-weight: 700; text-align: center;
      transition: 0.3s; outline: none;
    }
    .ai-otp-input:focus {
      border-color: #10b981; background: #ffffff;
      box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1);
    }

    .ai-timer-box { text-align: center; margin-bottom: 24px; }
    .ai-timer-badge {
      display: inline-flex; align-items: center; gap: 8px;
      background: #f0fdf4; color: #10b981;
      padding: 6px 14px; border-radius: 20px;
      font-size: 14px; font-weight: 700;
    }

    .db-modern-btn {
      background: linear-gradient(135deg, #10b981, #059669);
      color: #fff; border: none; padding: 16px; border-radius: 14px;
      font-weight: 700; width: 100%; font-size: 16px; letter-spacing: 0.5px;
      box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3); transition: 0.3s;
      display: flex; align-items: center; justify-content: center; gap: 8px;
    }
    .db-modern-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4); }
    .db-modern-btn:disabled { opacity: 0.7; cursor: not-allowed; }

    .ai-resend-link {
      display: block; text-align: center; margin-top: 24px;
      color: #64748b; font-size: 14px; font-weight: 600;
      background: none; border: none; cursor: pointer; transition: 0.3s;
    }
    .ai-resend-link:hover:not(:disabled) { color: #10b981; text-decoration: underline; }
    .ai-resend-link:disabled { opacity: 0.5; cursor: default; }
  `;

  if (!show) return null;

  return (
    <>
      <style>{customStyles}</style>
      <div className="ai-modal-overlay">
        <div className="ai-auth-card">
          <button className="ai-close-btn" onClick={() => { setShow(false); navigate("/"); }}>
            <i className="fa fa-times"></i>
          </button>

          <div className="ai-icon-bubble">
            <i className="fa fa-envelope-open-text"></i>
          </div>

          <h2 className="ai-title">OTP Verification</h2>
          <p className="ai-subtitle">
            Enter the 6-digit code we've sent to <br />
            <span className="text-dark fw-bold">{email || "your email"}</span>
          </p>

          <form onSubmit={verifyOTP}>
            <div className="ai-otp-grid">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <input
                  key={i}
                  type="text"
                  maxLength={1}
                  className="ai-otp-input"
                  ref={(el) => (inputsRef.current[i] = el)}
                  onInput={(e) => handleInput(e, i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  required
                />
              ))}
            </div>

            <div className="ai-timer-box">
              {timer > 0 ? (
                <div className="ai-timer-badge">
                  <i className="fa fa-clock"></i>
                  {formatTime()}
                </div>
              ) : (
                <span className="text-danger fw-bold small">Code expired</span>
              )}
            </div>

            <button type="submit" className="db-modern-btn" disabled={isVerifying || timer === 0}>
              {isVerifying ? "Verifying..." : "Verify OTP"}
            </button>

            <button
              type="button"
              className="ai-resend-link"
              onClick={resendOTP}
              disabled={!canResend}
            >
              {canResend ? "Didn't receive code? Resend" : `Resend in ${formatTime()}`}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}